import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  DialogActions,
  Button,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { RHFTextField } from '../hook-form';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider from '../hook-form/form-provider';
import { getRequest, postRequest } from 'src/services';
import * as Yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { baseURL } from 'src/config-global';
import { handleErrorMessages } from '../risk-management/risk-register/feching';
import Iconify from '../iconify';
import { DatePicker } from '@mui/x-date-pickers';
import { endpoints } from 'src/utils/axios';
import axios from 'axios';
import { differenceInCalendarDays } from 'date-fns';

const EscalationModal = ({ isOpen, handleClose, reload, PercentIcon }) => {
  const [loading, setLoading] = useState(false);
  const NewUserSchema = Yup.object().shape({
    escalation_code: Yup.string().required('Escalation code is required'),
    estimate_date: Yup.date()
      .nullable()
      .required('Estimate date is required')
      .test(
        'estimate-before-or-equal-start',
        'Estimate date must be before or equal to the start date',
        function (value) {
          const startDate = this.parent.start_date;
          if (!value || !startDate) return true;
          return new Date(value).setHours(0, 0, 0, 0) <= new Date(startDate).setHours(0, 0, 0, 0);
        }
      ),
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().min(
      Yup.ref('start_date'),
      'End date must be after or equal to the start date'
    ),
    rate_of_escalation: Yup.number().required('Rate of escalation is required'),
  });

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues: {
      escalation_code: '',
      estimate_date: new Date(),
      contract_duration: '',
      start_date: new Date(),
      end_date: new Date(),
      total_duration: '',
      mid_point_of_construction: '',
      mid_date: null,
      rate_of_escalation: '',
      mid_date_year: null,
      escalation: '',
    },
  });

  const { reset, watch, setValue, handleSubmit, warning, control } = methods;
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const values = watch();

  useEffect(() => {
    getProjectDetails();
  }, []);

  const getProjectDetails = async () => {
    try {
      const response = await axios.get(`${baseURL}${endpoints.chat_details}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
          currentCompanyId: localStorage.getItem('companyId'),
          currentProjectId: localStorage.getItem('projectId'),
        },
      });
      const { notice_to_proceed_date, end_date } = response.data.project;
      const startDate = notice_to_proceed_date ? new Date(notice_to_proceed_date) : new Date();
      const endDate = end_date ? new Date(end_date) : new Date();

      setValue('start_date', startDate);
      setValue('end_date', endDate);
      calculateContractDuration(startDate, endDate);
      calculateTotalDuration(values.estimate_date, endDate);
    } catch (error) {
      console.error('Error fetching project details:', error);
    }
  };

  const calculateContractDuration = useCallback(
    (startDate, endDate) => {
      const durationInDays = differenceInCalendarDays(endDate, startDate) + 1;
      setValue('contract_duration', durationInDays);
    },
    [setValue, values]
  );

  const calculateTotalDuration = useCallback(
    (estimateDate, endDate) => {
      const durationInDays = differenceInCalendarDays(endDate, estimateDate) + 1;
      setValue('total_duration', durationInDays);
    },
    [setValue, values]
  );

  const onClose = () => {
    handleClose();
    reset();
  };

  const onSubmit = handleSubmit(async (formData) => {
    setLoading(true);
    try {
      const responseData = await postRequest('', formData);
      if (responseData.metadata && responseData.metadata.success) {
        onClose();
        reload();
        enqueueSnackbar('Contingency created successfully', { variant: 'success' });
      } else {
        handleErrorMessages(responseData.metadata.message);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      enqueueSnackbar('An error occurred while submitting the form.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  });

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxSizing: 'content-box',
          }}
        >
          <span
            className="cardColor"
            style={{ padding: '7px', marginLeft: '-25px', color: 'white', fontWeight: '700' }}
          >
            &nbsp;&nbsp; Create Escalation &nbsp;&nbsp;&nbsp;&nbsp;
          </span>
          <IconButton onClick={handleClose}>
            <Iconify icon="mdi:close" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box
            mt={2}
            mb={2}
            gap={3}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(2, 1fr)',
              lg: 'repeat(2, 1fr)',
            }}
          >
            <RHFTextField name="escalation_code" label="Escalation Code" required />
            <Controller
              name="estimate_date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Estimate Date"
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                    calculateTotalDuration(newValue, values.end_date);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error || !!warning,
                      helperText: error?.message || warning,
                    },
                  }}
                />
              )}
            />
            <RHFTextField
              type="number"
              name="contract_duration"
              label="Contract Duration"
              disabled
            />
            <Controller
              name="start_date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Project Start Date (NTP)"
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                    calculateContractDuration(newValue, values.end_date);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error || !!warning,
                      helperText: error?.message || warning,
                    },
                  }}
                />
              )}
            />
            <Controller
              name="end_date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Project End Date"
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                    calculateContractDuration(values.start_date, newValue);
                    calculateTotalDuration(values.estimate_date, newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error || !!warning,
                      helperText: error?.message || warning,
                    },
                  }}
                />
              )}
            />
            <RHFTextField
              type="number"
              name="total_duration"
              label="Total Duration From Estimate Date"
              disabled
            />
            <RHFTextField
              type="number"
              name="mid_point_of_construction"
              label="Mid Point Of Construction"
              disabled
            />
            <Controller
              name="mid_date"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Mid Point Date  Of Construction "
                  value={field.value}
                  disabled
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error || !!warning,
                      helperText: error?.message || warning,
                    },
                  }}
                />
              )}
            />
            <RHFTextField
              type="number"
              name="rate_of_escalation"
              label="Rate of Escalation"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {PercentIcon}
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
            <Controller
              name="mid_date_year"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Mid Point Duration From Estimate Date (Years)"
                  value={field.value}
                  disabled
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error || !!warning,
                      helperText: error?.message || warning,
                    },
                  }}
                />
              )}
            />
            <RHFTextField
              type="number"
              name="escalation"
              label="Escalation %"
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {PercentIcon}
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            className="dark-button"
            disabled={loading}
            loading={loading}
            type="submit"
            variant="contained"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};
export default EscalationModal;
